import { Button, type ButtonProps, IconButton, LuIcon, Menu } from "@overmap-ai/blocks"
import { useImageCapturer } from "@overmap-ai/forms"
import { Camera, Paperclip } from "lucide-react"
import { type ChangeEventHandler, memo, useCallback } from "react"

import { useFileInputRef } from "../../hooks/utils"

interface AttachmentButtonProps extends ButtonProps {
	onAttachFiles: (files: File[]) => void
	icon?: boolean
}

export const AttachmentButton = memo((props: AttachmentButtonProps) => {
	const { children, icon = false, onAttachFiles, size, ...rest } = props
	const [inputRef, toggleInputRef] = useFileInputRef()
	const openImageCapturer = useImageCapturer()

	const handleTakePhotos = useCallback(() => {
		openImageCapturer((close) => ({
			onConfirm: onAttachFiles,
			onClose: close,
		}))
	}, [openImageCapturer, onAttachFiles])

	const handleFileInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		(e) => {
			const { files: filesList } = e.target
			if (!filesList || !filesList.length) return

			const files = Array.from(filesList)
			onAttachFiles(files)
		},
		[onAttachFiles],
	)

	const ButtonComponent = icon ? IconButton : Button

	return (
		<>
			<Menu.Root>
				<Menu.ClickTrigger>
					<ButtonComponent size={size} {...rest}>
						{children}
					</ButtonComponent>
				</Menu.ClickTrigger>
				<Menu.Content size={size}>
					<Menu.Item onSelect={handleTakePhotos}>
						<LuIcon icon={Camera} />
						Take photos
					</Menu.Item>
					<Menu.Item onSelect={toggleInputRef}>
						<LuIcon icon={Paperclip} />
						Attach files
					</Menu.Item>
				</Menu.Content>
			</Menu.Root>
			<input className="hidden" ref={inputRef} onChange={handleFileInputChange} type="file" multiple />
		</>
	)
})
AttachmentButton.displayName = "AttachmentButton"
